import { useEffect, useRef } from "react";
import "./SlotGame.css";
import { drawState } from "../../constant/General";

export default function SlotGame({
    slot,
    index,
    config,
    isNight = false,
    drawStatus,
    onAfterReleaseCallback,
}) {
    const spinningRefs = useRef({});
    const CELL = isNight ? 83 : 61;
    const CYCLE = CELL * 10; // 0-9 repeated

    const getSlot = isNight ? "ng" : "day";
    const configSlot = isNight ? config.ng.slot : config.slot;
    const isRelease = drawStatus === drawState.release;
    const isDrawing = drawStatus === drawState.drawing;

    const REELS = slot ? String(slot).length : isNight ? 5 : 2;
    const arrNum = Array.from({ length: REELS }, (_, i) => i);
    const gapSlot = configSlot[`slot_gap_${REELS}`];
    const styleSlot = configSlot[`slot_${REELS}`];
    const styleWrap = isNight ? { top: "21px", height: "83px" } : { top: "16px", height: "61px" };
    const getGif = isRelease ? `Front-light${REELS}` : `F${REELS}`;
    const pattern = slot ? String(slot).split("").map(Number) : [];

    const startLoop = (i) => {
        if (spinningRefs.current[i]) return;
        const reel = document.getElementById(`reel-${index}-${i}`);
        if (!reel) return;
        reel.style.transition = "none";
        reel.style.animation = "spinLoop 1.7s linear infinite reverse"; // reverse makes motion downward (0→9)
        spinningRefs.current[i] = true;
    };

    const stopAt = (i, digit) => {
        return new Promise((resolve) => {
            const reel = document.getElementById(`reel-${index}-${i}`);
            if (!reel) return resolve();

            // halt loop animation
            reel.style.animation = "none";
            spinningRefs.current[i] = false;

            // map digit: we render 9→0 top to bottom so downward motion shows 0→9
            const canonicalTarget = -(9 - digit) * CELL;
            // predictable, shorter stop path: start one cycle above the target to reduce jerkiness
            const startY = canonicalTarget - CYCLE; // one full loop gives a visible but tighter coast
            const animatedTarget = canonicalTarget;

            reel.style.transition = "none";
            reel.style.transform = `translateY(${startY}px)`;

            // smooth JS tween to avoid transition snap quirks
            const duration = (2.2 + i * 0.08) * 1000;
            const start = performance.now();
            const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

            const animate = (now) => {
                const elapsed = now - start;
                const t = Math.min(1, elapsed / duration);
                const eased = easeOutCubic(t);
                const current = startY + (animatedTarget - startY) * eased;
                reel.style.transform = `translateY(${current}px)`;
                if (t < 1) {
                    requestAnimationFrame(animate);
                } else {
                    resolve();
                }
            };

            requestAnimationFrame(animate);
        });
    };

    useEffect(() => {
        if (isDrawing) {
            (async () => {
                for (let i = 0; i < arrNum.length; i++) {
                    await new Promise((r) => setTimeout(r, 500));
                    startLoop(i);
                }
            })();
            return;
        }

        if (isRelease && slot) {
            arrNum.forEach((i) => startLoop(i));
            (async () => {
                for (let i = 0; i < pattern.length; i++) {
                    await new Promise((r) => setTimeout(r, 200)); // slimmer stagger so slow ease isn't just extra wait
                    await stopAt(i, pattern[i]);
                    if (i === pattern.length - 1) {
                        setTimeout(() => {
                            onAfterReleaseCallback();
                        }, 800);
                    }
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawStatus, slot]);

    return (
        <div className="container text-center d-flex justify-content-center align-items-center">
            <div
                className="d-flex justify-content-center position-relative live-slot-background"
                style={{
                    height: isNight ? "125px" : "94px",
                    width: isNight ? "420px" : "169px",
                    backgroundSize: "cover",
                    backgroundImage: `url(${require(`../../assets/images/slot/${getSlot}/${getGif}.gif`)})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                }}
            >
                <div
                    className="d-flex justify-content-center align-items-center position-absolute custom-gap"
                    style={{ ...styleWrap, ...gapSlot }}
                >
                    {arrNum.map((i) => (
                        <div className="slot-box" key={i} style={{ height: isNight ? "83px" : "" }}>
                            <div className="reel" id={`reel-${index}-${i}`}>
                                {Array.from({ length: 60 }).map((_, idx) => {
                                    const digitVal = 9 - (idx % 10); // descending so downward motion shows 0→9
                                    return (
                                        <div key={idx} className="reel-number" style={{ ...styleSlot, height: isNight ? "83px" : "" }}>
                                            {digitVal}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
